<template>
  <div class="incident-dispatch-panel">
    <!-- Header -->
    <div class="dispatch-header">
      <v-icon small color="var(--kln-accent)" class="mr-2">mdi-truck-fast-outline</v-icon>
      <div class="dispatch-header__titles">
        <span class="dispatch-header__title">Despacho</span>
        <span class="dispatch-header__sub">Incidente {{ folioPrincipal || incidentId }}</span>
      </div>
      <v-spacer />
      <v-btn icon x-small @click="onClose">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Body -->
    <div class="dispatch-body">
      <div v-if="!corporaciones.length" class="dispatch-empty">
        <v-icon small color="var(--kln-text-secondary)" class="mr-1">mdi-information-outline</v-icon>
        Este incidente no tiene corporaciones asignadas.
      </div>

      <div
        v-for="(corp, idx) in corporaciones"
        :key="(corp.id || corp.corporacion) + '-' + idx"
        class="corp-card"
        :class="{
          'corp-card--closed': corp.emergenciaCerrada,
          'corp-card--virtual': corp._virtual,
        }"
      >
        <div class="corp-card__head">
          <div class="corp-card__titles">
            <span class="corp-card__name">
              {{ corp.corporacion }}
              <span v-if="corp._virtual" class="corp-card__dev-tag">DEV</span>
            </span>
            <span class="corp-card__folio">
              {{ corp._virtual ? 'Devices reales del mapa (prueba de ruta OSRM)' : 'Folio: ' + (corp.folioEmergencia || '—') }}
            </span>
          </div>
          <span
            v-if="!corp._virtual"
            class="corp-card__status"
            :class="{ 'corp-card__status--closed': corp.emergenciaCerrada }"
          >{{ corp.emergenciaCerrada ? 'Cerrado' : 'Abierto' }}</span>
          <v-menu v-if="canCloseIncident(corp) && !corp._virtual" offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" class="corp-card__kebab">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense dark>
              <v-list-item @click="openCancelDialog(corp)">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="warning">mdi-cancel</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Cancelar</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openCloseDialog(corp)">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="error">mdi-close-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Cerrar</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <!-- Corp virtual PRUEBAS: en lugar del listado de unidades CAD,
             mostramos un autocomplete buscable con TODOS los devices del
             mapa. Al seleccionar uno se traza la ruta OSRM y aparece como
             unit-row debajo. -->
        <div v-if="corp._virtual" class="corp-units">
          <v-autocomplete
            :value="testSelectedUnit"
            :items="getTestDevices()"
            item-text="unit"
            item-value="unit"
            placeholder="Buscar device por nombre (ej. tetra, DSP, ME015)..."
            outlined dense hide-details
            clearable
            return-object
            no-data-text="Sin devices con coordenadas en el mapa"
            :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 280 }"
            class="test-corp-search"
            @input="onTestDeviceSelect($event, corp)"
          >
            <template #item="{ item }">
              <div class="test-corp-search__item">
                <span class="test-corp-search__name">{{ item.unit }}</span>
                <span class="test-corp-search__status" :class="'test-corp-search__status--' + item.status">
                  {{ item.status }}
                </span>
              </div>
            </template>
          </v-autocomplete>
          <div v-if="testSelectedUnit" class="unit-row unit-row--active">
            <div class="unit-row__head">
              <span class="unit-row__name">{{ testSelectedUnit.unit }}</span>
              <span class="unit-row__status" :style="unitStatusStyle(testSelectedUnit.status)">
                {{ testSelectedUnit.status || '—' }}
              </span>
            </div>
            <div v-if="activeRouteBadge" class="route-badge">
              <v-icon x-small class="mr-1" color="var(--kln-accent)">mdi-routes</v-icon>
              <span class="route-badge__distance">{{ activeRouteBadge.distance }}</span>
              <span class="route-badge__sep">·</span>
              <span class="route-badge__duration">{{ activeRouteBadge.duration }}</span>
              <span class="route-badge__note">sin tráfico</span>
            </div>
          </div>
        </div>

        <!-- Lista de unidades despachadas (corp real). Clic en la row pide
             al mapa trazar la ruta OSRM desde la unidad hasta el incidente. -->
        <div v-else-if="getUnidades(corp.corporacion).length" class="corp-units">
          <div
            v-for="(unit, ui) in getUnidades(corp.corporacion)"
            :key="(unit.unit || 'u') + '-' + ui"
            class="unit-row"
            :class="{ 'unit-row--active': activeRouteUnit === unit.unit }"
            @click="onUnitRowClick(corp, unit)"
            v-auto-tooltip="'Ver ruta de esta unidad hacia el incidente'"
          >
            <div class="unit-row__head">
              <span class="unit-row__name">{{ unit.unit || unit.identifier || '—' }}</span>
              <!-- Status chip: clickable si hay siguiente transición; estática si Libre/desconocido -->
              <v-menu
                v-if="!corp._virtual && canChangeUnitStatus(corp, unit)"
                offset-y
                bottom
              >
                <template #activator="{ on, attrs }">
                  <button
                    type="button"
                    class="unit-row__status unit-row__status--clickable"
                    :style="unitStatusStyle(unit.status)"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop
                  >
                    {{ unit.status || '—' }}
                    <v-icon x-small class="ml-1" :color="STATUS_COLORS[unit.status] || '#9e9e9e'">mdi-menu-down</v-icon>
                  </button>
                </template>
                <v-list dense dark>
                  <v-list-item
                    v-for="next in nextStatusesFor(unit.status)"
                    :key="next.identifier"
                    @click="confirmChangeUnitStatus(corp, unit, next)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small :color="STATUS_COLORS[next.statdesc] || 'grey'">mdi-arrow-right-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ next.statdesc }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <span
                v-else
                class="unit-row__status"
                :style="unitStatusStyle(unit.status)"
              >{{ unit.status || '—' }}</span>
            </div>
            <div class="unit-row__times">
              <span v-if="getDespacho(unit)" class="unit-time unit-time--despacho">{{ getDespacho(unit) }}</span>
              <span v-if="getLlegada(unit)" class="unit-time unit-time--llegada">{{ getLlegada(unit) }}</span>
              <span v-if="getLiberado(unit)" class="unit-time unit-time--libre">{{ getLiberado(unit) }}</span>
            </div>
            <div v-if="activeRouteUnit === unit.unit && activeRouteBadge" class="route-badge">
              <v-icon x-small class="mr-1" color="var(--kln-accent)">mdi-routes</v-icon>
              <span class="route-badge__distance">{{ activeRouteBadge.distance }}</span>
              <span class="route-badge__sep">·</span>
              <span class="route-badge__duration">{{ activeRouteBadge.duration }}</span>
              <span class="route-badge__note">sin tráfico</span>
            </div>
          </div>
        </div>
        <div v-else-if="!corp._virtual" class="corp-units corp-units--empty">Sin unidades asignadas</div>

        <!-- Botón Asignar unidad. Visible aún cuando la corp está cerrada
             para permitir continuar el seguimiento operativo (liberar la
             última unidad, asignar otra) — paridad con el flujo CAD
             legacy donde una corp "cerrada" no bloquea el despacho.
             Se oculta para la corp virtual PRUEBAS (no aplica). -->
        <button
          v-if="canAssignUnits(corp.corporacion) && !corp._virtual"
          type="button"
          class="corp-card__assign-btn"
          @click="openAssignDialog(corp)"
        >
          <v-icon x-small class="mr-1">mdi-plus</v-icon>Asignar unidad
        </button>
      </div>
    </div>

    <!-- Modal Asignar Unidad -->
    <v-dialog v-model="assignDialogOpen" max-width="480" persistent>
      <v-card class="assign-dialog">
        <v-card-title class="assign-dialog__title">
          <v-icon left>mdi-account-plus-outline</v-icon>
          <span>Asignar unidad</span>
          <v-spacer />
          <v-btn icon small :disabled="assignSaving" @click="closeAssignDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="assign-dialog__body">
          <div class="assign-dialog__readonly">
            <div><strong>Folio:</strong> {{ assignTarget && assignTarget.folioEmergencia || '—' }}</div>
            <div><strong>Corporación:</strong> {{ assignTarget && assignTarget.corporacion || '—' }}</div>
          </div>
          <div class="assign-dialog__field">
            <label class="assign-dialog__label">Unidad</label>
            <v-autocomplete
              v-model="assignUnit"
              :items="unitsCatalog"
              :loading="unitsLoading"
              item-text="identifier"
              item-value="identifier"
              return-object
              outlined dense hide-details
              clearable
              no-data-text="Sin unidades disponibles"
              :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 240 }"
            />
          </div>
          <div class="assign-dialog__field">
            <label class="assign-dialog__label">Estado</label>
            <v-autocomplete
              v-model="assignStatus"
              :items="statusCatalogDespacho"
              :loading="statusLoading"
              item-text="statdesc"
              item-value="identifier"
              outlined dense hide-details
              :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 220 }"
            />
          </div>
          <div v-if="assignError" class="assign-dialog__error">
            <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
            {{ assignError }}
          </div>
        </v-card-text>
        <v-card-actions class="assign-dialog__footer">
          <v-btn text small :disabled="assignSaving" @click="closeAssignDialog">Cancelar</v-btn>
          <v-spacer />
          <v-btn
            color="var(--kln-accent)"
            small
            :loading="assignSaving"
            :disabled="assignSaving || !canSubmitAssign"
            @click="saveAssignUnit"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Cancelar incidente -->
    <v-dialog v-model="cancelDialogOpen" max-width="480" persistent>
      <v-card class="assign-dialog">
        <v-card-title class="assign-dialog__title">
          <v-icon left color="warning">mdi-cancel</v-icon>
          <span>Cancelar incidente</span>
          <v-spacer />
          <v-btn icon small :disabled="cancelSaving" @click="closeCancelDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="assign-dialog__body">
          <div class="assign-dialog__readonly">
            <div><strong>Folio:</strong> {{ cancelTarget && cancelTarget.folioEmergencia || '—' }}</div>
            <div><strong>Corporación:</strong> {{ cancelTarget && cancelTarget.corporacion || '—' }}</div>
          </div>
          <div class="assign-dialog__field">
            <label class="assign-dialog__label">Código de cancelación</label>
            <v-autocomplete
              v-model="cancelCode"
              :items="cancelCodesCatalog"
              :loading="cancelCodesLoading"
              item-text="desc"
              item-value="identifier"
              outlined dense hide-details
              clearable
              no-data-text="Sin códigos disponibles"
              :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 240 }"
            />
          </div>
          <div class="assign-dialog__field">
            <label class="assign-dialog__label">Motivo (opcional)</label>
            <v-textarea
              v-model="cancelReason"
              outlined dense hide-details
              rows="3"
              counter="500"
              maxlength="500"
              auto-grow
              placeholder="Describe el motivo de la cancelación..."
            />
          </div>
          <div v-if="cancelError" class="assign-dialog__error">
            <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
            {{ cancelError }}
          </div>
        </v-card-text>
        <v-card-actions class="assign-dialog__footer">
          <v-btn text small :disabled="cancelSaving" @click="closeCancelDialog">Volver</v-btn>
          <v-spacer />
          <v-btn
            color="warning"
            small
            :loading="cancelSaving"
            :disabled="cancelSaving || !cancelCode"
            @click="saveCancelIncident"
          >
            <v-icon left small>mdi-cancel</v-icon>
            Cancelar incidente
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Cerrar incidente -->
    <v-dialog v-model="closeDialogOpen" max-width="480" persistent>
      <v-card class="assign-dialog">
        <v-card-title class="assign-dialog__title">
          <v-icon left color="error">mdi-close-circle-outline</v-icon>
          <span>Cerrar incidente</span>
          <v-spacer />
          <v-btn icon small :disabled="closeSaving" @click="closeCloseDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="assign-dialog__body">
          <div class="assign-dialog__readonly">
            <div><strong>Folio:</strong> {{ closeTarget && closeTarget.folioEmergencia || '—' }}</div>
            <div><strong>Corporación:</strong> {{ closeTarget && closeTarget.corporacion || '—' }}</div>
          </div>
          <div class="assign-dialog__field">
            <label class="assign-dialog__label">Clearance</label>
            <v-autocomplete
              v-model="closeClearance"
              :items="clearancesCatalog"
              :loading="clearancesLoading"
              item-text="desc"
              item-value="identifier"
              outlined dense hide-details
              clearable
              no-data-text="Sin clearances disponibles"
              :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 240 }"
            />
          </div>
          <div v-if="closeError" class="assign-dialog__error">
            <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
            {{ closeError }}
          </div>
        </v-card-text>
        <v-card-actions class="assign-dialog__footer">
          <v-btn text small :disabled="closeSaving" @click="closeCloseDialog">Volver</v-btn>
          <v-spacer />
          <v-btn
            color="error"
            small
            :loading="closeSaving"
            :disabled="closeSaving || !closeClearance"
            @click="saveCloseIncident"
          >
            <v-icon left small>mdi-close-circle-outline</v-icon>
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from '@/util/dayjs-config'
import cadCatalogsService from '@/util/cad-catalogs.service'

const STATUS_COLORS = {
  Despacho: '#ef5350',
  Llegada: '#facc15',
  Libre: '#9e9e9e',
}

// State machine forward-only: Despacho(1) → Llegada(2) → Libre(4). Mismo
// que legacy IncidentDispatch.vue:746-754 (getUnitsStatusCatalog).
const STATUS_NEXT_BY_CURRENT = {
  Despacho: '2',
  Llegada: '4',
  // Libre: sin transición (terminal)
  '1': '2',
  '2': '4',
}

export default {
  name: 'IncidentDispatchPanel',

  inject: {
    // Provisto por MapV2: getter que retorna una lista sintética de devices
    // del mapa para la corp virtual "PRUEBAS". Default no-op para no romper
    // si el panel se monta fuera de MapV2 (ej. tests).
    getTestDevices: { default: function() { return [] } },
  },

  props: {
    incident: {
      type: Object,
      default: null,
    },
    // Info de la ruta activa (calculada en MapV2 desde la response OSRM)
    // que mostramos como badge "X.X km · Y min (sin tráfico)" cuando hay
    // una unidad con ruta dibujada.
    activeRouteEntityId: { type: [String, Number], default: null },
    activeRouteDistance: { type: Number, default: null }, // metros
    activeRouteDuration: { type: Number, default: null }, // segundos
  },

  data() {
    return {
      STATUS_COLORS,
      // Asignar unidad
      assignDialogOpen: false,
      assignTarget: null,
      assignUnit: null,
      assignStatus: '1',
      assignSaving: false,
      assignError: '',
      unitsCatalog: [],
      unitsLoading: false,
      statusCatalog: [],
      statusLoading: false,
      // Cancelar incidente
      cancelDialogOpen: false,
      cancelTarget: null,
      cancelCode: null,
      cancelReason: '',
      cancelSaving: false,
      cancelError: '',
      cancelCodesCatalog: [],
      cancelCodesLoading: false,
      // Cerrar incidente
      closeDialogOpen: false,
      closeTarget: null,
      closeClearance: null,
      closeSaving: false,
      closeError: '',
      clearancesCatalog: [],
      clearancesLoading: false,
      // Nombre de la unidad cuya ruta está activa en el mapa (highlight
      // visual en el panel). Se limpia al cambiar de incidente.
      activeRouteUnit: null,
      // Device seleccionado en el autocomplete de la corp virtual PRUEBAS
      // (objeto entero, no solo el nombre — para mostrar status, etc.).
      testSelectedUnit: null,
    }
  },

  computed: {
    ...mapGetters('auth', ['getUser']),

    incidentData() {
      // selectedItem tiene dos niveles: item.data = monitor MapV2,
      // monitor.data = raw del backend (con .attributes). Mismo unwrap
      // que MapDrawer:2372-2375 — sin esto incidentAttrs queda vacío y
      // el panel piensa que el incidente no tiene corporaciones.
      var monitor = (this.incident && this.incident.data) || {}
      return monitor.data || monitor || {}
    },
    incidentAttrs() {
      return (this.incidentData && this.incidentData.attributes) || {}
    },
    incidentId() {
      return this.incidentData.id || this.incidentData._id || ''
    },
    folioPrincipal() {
      var c = this.corporaciones[0]
      return (c && c.folioEmergencia) || ''
    },
    corporaciones() {
      // Normaliza los 3 shapes posibles del backend a array de objetos
      // {corporacion, folioEmergencia, emergenciaCerrada}:
      //   1. Array de objetos (caso CAD completo del backend)
      //   2. Array de strings ["CRUM", "CMMNAU"] (caso ligero)
      //   3. String CSV "CRUM, CMMNAU, PE" (caso simplificado)
      var raw = this.incidentAttrs.corporaciones
      var real = []
      if (raw) {
        var arr = raw
        if (typeof raw === 'string') {
          arr = raw.split(',').map(function(s) { return s.trim() }).filter(Boolean)
        }
        if (Array.isArray(arr)) {
          real = arr.map(function(c) {
            if (typeof c === 'string') return { corporacion: c }
            return c
          })
        }
      }
      // Corp virtual "PRUEBAS": permite validar el flujo OSRM (ruta + fit
      // bounds + highlight) usando devices reales del mapa, sin depender
      // del mapeo CAD→device.cadId que casi nunca está poblado en este
      // deployment. Las "unidades" de PRUEBAS son los devices con coords
      // del mapa (cap 30) — ver MapV2.provide.getTestDevices.
      real.push({
        corporacion: 'PRUEBAS',
        folioEmergencia: 'TEST',
        emergenciaCerrada: false,
        _virtual: true,
      })
      return real
    },
    cadCenter() {
      return this.incidentAttrs.subcentro || ''
    },
    statusCatalogDespacho() {
      return this.statusCatalog.filter((s) => String(s.identifier) === '1')
    },
    canSubmitAssign() {
      return !!(this.assignUnit && this.assignStatus && this.assignTarget)
    },

    // Texto del badge de ruta: "12.3 km · 18 min" basado en la response
    // OSRM. Devuelve null si no hay ruta activa. La etiqueta "sin tráfico"
    // la pinta el template — el dato OSRM nunca incluye factor de tráfico.
    activeRouteBadge() {
      if (this.activeRouteDistance == null || this.activeRouteDuration == null) return null
      var km = this.activeRouteDistance / 1000
      var distText = km >= 10
        ? km.toFixed(0) + ' km'
        : km.toFixed(1) + ' km'
      var totalMin = Math.round(this.activeRouteDuration / 60)
      var timeText
      if (totalMin < 1) timeText = '< 1 min'
      else if (totalMin < 60) timeText = totalMin + ' min'
      else {
        var hh = Math.floor(totalMin / 60)
        var mm = totalMin % 60
        timeText = hh + ' h' + (mm ? ' ' + mm + ' min' : '')
      }
      return { distance: distText, duration: timeText }
    },
  },

  watch: {
    // Observamos el id real del incidente (incidentData.id, ya desempacado
    // de item.data.data) para recargar el catálogo de unit-status al
    // cambiar de incidente. Antes mirábamos incident.data.id (el monitorId),
    // que no cambia entre incidentes distintos del mismo entityType.
    incidentId: {
      immediate: true,
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this._loadStatusCatalog()
          // Cambio de incidente → la ruta activa anterior ya no aplica
          this.activeRouteUnit = null
          this.testSelectedUnit = null
        }
      },
    },
  },

  methods: {
    ...mapActions('map-v2', ['closeIncidentDispatch', 'refreshSelectedIncident']),

    onClose() {
      this.closeIncidentDispatch()
    },

    // Clic en una unidad asignada: pide al mapa trazar la ruta OSRM
    // desde la unidad hasta el incidente. MapV2 hace toda la lógica
    // (lookup del monitor por nombre + OSRM + render + fit bounds).
    // Aquí solo marcamos visualmente cuál unidad es la activa.
    onUnitRowClick(corp, unit) {
      if (!unit || !unit.unit) return
      this.activeRouteUnit = unit.unit
      this.$emit('trace-unit-route', {
        unitName: unit.unit,
        corporacion: corp.corporacion,
      })
    },

    // Selección en el autocomplete de la corp virtual PRUEBAS: dispara el
    // mismo flujo de ruta. Al limpiar (clearable de v-autocomplete) el
    // payload viene null → reseteamos Y emitimos clear-unit-route para
    // que el padre borre la ruta del mapa (la ruta dispatch no se
    // auto-limpia porque source='dispatch' la marca como persistente).
    onTestDeviceSelect(device, corp) {
      this.testSelectedUnit = device || null
      if (!device || !device.unit) {
        this.activeRouteUnit = null
        this.$emit('clear-unit-route')
        return
      }
      this.activeRouteUnit = device.unit
      this.$emit('trace-unit-route', {
        unitName: device.unit,
        corporacion: corp.corporacion,
      })
    },

    // ────────────────────────────────────────────────────────────────
    // Permisos
    // ────────────────────────────────────────────────────────────────

    // Match con user.attributes.corporaciones (allowlist). Si está vacío =
    // acceso a todas. Replica legacy IncidentDispatch:1094 (typo "ssss"
    // del original mantenido como comentario para trazabilidad).
    hasPermissionToCorp(corpName) {
      var u = this.getUser
      if (!u || !u.attributes) return false
      if (!u.attributes.cad_user) return false
      var allowed = u.attributes.corporaciones
      if (!Array.isArray(allowed) || allowed.length === 0) return true
      return allowed.includes(corpName)
    },

    canAssignUnits(corpName) {
      var u = this.getUser
      if (!u || !u.attributes) return false
      if (!u.attributes.cad_permission_assign_units) return false
      return this.hasPermissionToCorp(corpName)
    },

    canCloseIncident(corp) {
      var u = this.getUser
      if (!u || !u.attributes) return false
      if (corp.emergenciaCerrada) return false
      if (!u.attributes.cad_permission_close_incident) return false
      return this.hasPermissionToCorp(corp.corporacion)
    },

    canChangeUnitStatus(corp, unit) {
      // Se permite cambiar estatus aunque la corp esté cerrada — las
      // unidades despachadas siguen necesitando reflejar su estado real
      // (llegada, libre) durante todo su ciclo operativo, incluso si
      // administrativamente la corp ya cerró el evento.
      if (!this.canAssignUnits(corp.corporacion)) return false
      return this.nextStatusesFor(unit.status).length > 0
    },

    // ────────────────────────────────────────────────────────────────
    // State machine de unidades
    // ────────────────────────────────────────────────────────────────

    nextStatusesFor(currentStatus) {
      if (!currentStatus) return []
      var nextId = STATUS_NEXT_BY_CURRENT[currentStatus]
      if (!nextId) return []
      // Buscamos el item completo del catálogo para tener su statdesc
      return this.statusCatalog.filter(function(s) { return String(s.identifier) === String(nextId) })
    },

    // ────────────────────────────────────────────────────────────────
    // Catálogos (lazy)
    // ────────────────────────────────────────────────────────────────

    async _loadStatusCatalog() {
      if (!this.cadCenter) return
      this.statusLoading = true
      try {
        this.statusCatalog = await cadCatalogsService.getUnitStatusCatalog(this.cadCenter)
      } finally {
        this.statusLoading = false
      }
    },

    async _loadUnitsForCorp(corporacion) {
      this.unitsLoading = true
      try {
        this.unitsCatalog = await cadCatalogsService.getUnitsByCorporacion({
          center: this.cadCenter,
          corporacion,
          available: true,
        })
      } finally {
        this.unitsLoading = false
      }
    },

    async _loadCancelCodes() {
      if (this.cancelCodesCatalog.length || !this.cadCenter) return
      this.cancelCodesLoading = true
      try {
        this.cancelCodesCatalog = await cadCatalogsService.getCancelCodes(this.cadCenter)
      } finally {
        this.cancelCodesLoading = false
      }
    },

    async _loadClearances() {
      if (this.clearancesCatalog.length || !this.cadCenter) return
      this.clearancesLoading = true
      try {
        this.clearancesCatalog = await cadCatalogsService.getClearances(this.cadCenter)
      } finally {
        this.clearancesLoading = false
      }
    },

    // ────────────────────────────────────────────────────────────────
    // Asignar unidad
    // ────────────────────────────────────────────────────────────────

    async openAssignDialog(corp) {
      this.assignTarget = corp
      this.assignUnit = null
      this.assignStatus = '1'
      this.assignError = ''
      this.assignSaving = false
      this.unitsCatalog = []
      this.assignDialogOpen = true
      var ops = [this._loadUnitsForCorp(corp.corporacion)]
      if (!this.statusCatalog.length) ops.push(this._loadStatusCatalog())
      await Promise.all(ops)
    },

    closeAssignDialog() {
      if (this.assignSaving) return
      this.assignDialogOpen = false
      this.assignTarget = null
    },

    async saveAssignUnit() {
      if (!this.canSubmitAssign || this.assignSaving) return
      this.assignError = ''
      this.assignSaving = true
      var payload = {
        center: this.cadCenter,
        corporacion: this.assignTarget.corporacion,
        unit: this.assignUnit.identifier,
        status: this.assignStatus,
        folioEmergencia: this.assignTarget.folioEmergencia,
      }
      try {
        var updated = await cadCatalogsService.assignUnit(payload)
        // El backend devuelve el incidente completo actualizado. Aplicamos
        // ese raw al selectedItem para que la UI refleje el estado real,
        // incluyendo posibles reaperturas de corp (asignar unidad reabre
        // una corp cerrada) que el push optimista no podía detectar.
        this._applyServerUpdate(updated)
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Unidad asignada',
            color: 'success',
            icon: 'mdi-check-circle-outline',
          })
        }
        this.assignDialogOpen = false
        this.assignTarget = null
      } catch (err) {
        this.assignError = this._extractErr(err, 'No se pudo asignar la unidad. Intenta de nuevo.')
      } finally {
        this.assignSaving = false
      }
    },

    // ────────────────────────────────────────────────────────────────
    // Cambio de estatus de unidad despachada
    // ────────────────────────────────────────────────────────────────

    confirmChangeUnitStatus(corp, unit, nextStatus) {
      var self = this
      var unitName = unit.unit || '—'
      var hora = moment().format('HH:mm')
      this.$confirm({
        title: 'Cambiar estado de la unidad ' + unitName,
        description: 'Vas a marcar la unidad ' + unitName + ' (' + corp.corporacion
          + ') como "' + nextStatus.statdesc + '" a las ' + hora + '.\n\n¿Confirmas el cambio?',
        acceptText: 'Confirmar cambio',
      }).then(function() {
        self._doChangeUnitStatus(corp, unit, nextStatus)
      }).catch(function() { /* cancelado */ })
    },

    async _doChangeUnitStatus(corp, unit, nextStatus) {
      try {
        var updated = await cadCatalogsService.changeUnitStatus({
          incidentId: this.incidentData.id,
          corporacion: corp.corporacion,
          unit: unit.unit,
          status: nextStatus.identifier,
          center: this.cadCenter,
          folioEmergencia: corp.folioEmergencia,
        })
        // El backend devuelve el incidente entero con el statusHistory
        // actualizado y, si esta fue la última unidad activa de la corp,
        // posiblemente emergenciaCerrada=true por auto-cierre. Sin response
        // del backend no podríamos detectar esos efectos en cascada.
        this._applyServerUpdate(updated)
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Estado actualizado: ' + nextStatus.statdesc,
            color: 'success',
            icon: 'mdi-check-circle-outline',
          })
        }
      } catch (err) {
        if (window.VMA && window.VMA.showError) {
          window.VMA.showError({
            title: this._extractErr(err, 'No se pudo cambiar el estado de la unidad'),
          })
        }
      }
    },

    // ────────────────────────────────────────────────────────────────
    // Cancelar incidente por corp
    // ────────────────────────────────────────────────────────────────

    async openCancelDialog(corp) {
      this.cancelTarget = corp
      this.cancelCode = null
      this.cancelReason = ''
      this.cancelError = ''
      this.cancelSaving = false
      this.cancelDialogOpen = true
      this._loadCancelCodes()
    },

    closeCancelDialog() {
      if (this.cancelSaving) return
      this.cancelDialogOpen = false
      this.cancelTarget = null
    },

    async saveCancelIncident() {
      if (!this.cancelCode || this.cancelSaving) return
      this.cancelError = ''
      this.cancelSaving = true
      try {
        var updated = await cadCatalogsService.cancelIncident({
          incidentId: this.incidentData.id,
          corporacion: this.cancelTarget.corporacion,
          reason: (this.cancelReason || '').trim(),
          cancelCode: String(this.cancelCode).trim(),
          center: this.cadCenter,
        })
        this._applyServerUpdate(updated)
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Incidente cancelado',
            color: 'success',
            icon: 'mdi-check-circle-outline',
          })
        }
        this.cancelDialogOpen = false
        this.cancelTarget = null
      } catch (err) {
        this.cancelError = this._extractErr(err, 'No se pudo cancelar el incidente. Intenta de nuevo.')
      } finally {
        this.cancelSaving = false
      }
    },

    // ────────────────────────────────────────────────────────────────
    // Cerrar incidente por corp
    // ────────────────────────────────────────────────────────────────

    async openCloseDialog(corp) {
      this.closeTarget = corp
      this.closeClearance = null
      this.closeError = ''
      this.closeSaving = false
      this.closeDialogOpen = true
      this._loadClearances()
    },

    closeCloseDialog() {
      if (this.closeSaving) return
      this.closeDialogOpen = false
      this.closeTarget = null
    },

    async saveCloseIncident() {
      if (!this.closeClearance || this.closeSaving) return
      this.closeError = ''
      this.closeSaving = true
      try {
        var updated = await cadCatalogsService.closeIncident({
          incidentId: this.incidentData.id,
          corporacion: this.closeTarget.corporacion,
          clearance: String(this.closeClearance).trim(),
          center: this.cadCenter,
        })
        this._applyServerUpdate(updated)
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Incidente cerrado',
            color: 'success',
            icon: 'mdi-check-circle-outline',
          })
        }
        this.closeDialogOpen = false
        this.closeTarget = null
      } catch (err) {
        this.closeError = this._extractErr(err, 'No se pudo cerrar el incidente. Intenta de nuevo.')
      } finally {
        this.closeSaving = false
      }
    },

    // ────────────────────────────────────────────────────────────────
    // Helpers
    // ────────────────────────────────────────────────────────────────

    // Aplica el incidente devuelto por el backend al store (selectedItem).
    // El backend es la única fuente de verdad: una acción puede tener
    // efectos en cascada que el frontend no puede predecir (asignar
    // unidad reabre corp cerrada, cerrar última unidad puede auto-cerrar
    // corp, etc.). Por eso no hacemos push optimista: usamos la response.
    //
    // El backend devuelve el incidente entero; si por alguna razón devuelve
    // algo más simple (ej. solo {attributes}), lo wrappemos respetando el id.
    _applyServerUpdate(updated) {
      if (!updated) return
      // Caso A: respuesta es el incidente entero con id + attributes
      if (updated.id || updated._id) {
        this.refreshSelectedIncident(updated)
        return
      }
      // Caso B: respuesta es solo {attributes} — wrappemos con el id actual
      if (updated.attributes) {
        this.refreshSelectedIncident({
          id: this.incidentData.id,
          _id: this.incidentData._id,
          ...this.incidentData,
          attributes: updated.attributes,
        })
      }
    },

    _extractErr(err, fallback) {
      return (err && err.response && err.response.data
        && (err.response.data.message || err.response.data.error))
        || fallback
    },

    // ────────────────────────────────────────────────────────────────
    // Unidades
    // ────────────────────────────────────────────────────────────────

    getUnidades(corporacion) {
      // Corp virtual PRUEBAS: devuelve devices reales del mapa para
      // permitir validar el flujo de ruta OSRM end-to-end sin necesidad
      // de mapeo CAD en el backend.
      if (corporacion === 'PRUEBAS') {
        return this.getTestDevices()
      }
      var arr = this.incidentAttrs.unidades
      if (!Array.isArray(arr)) return []
      return arr.filter((u) => u && u.corporacion === corporacion)
    },

    unitStatusStyle(status) {
      var color = STATUS_COLORS[status] || '#9e9e9e'
      return {
        background: color + '22',
        color: color,
        borderColor: color + '88',
      }
    },

    getDespacho(unit) {
      var dt = unit.statusHistory && unit.statusHistory.find((s) => s.status === 'Despacho')
      return dt && dt.dateTime ? 'Despacho: ' + moment(dt.dateTime).format('HH:mm') : ''
    },
    getLlegada(unit) {
      var dt = unit.statusHistory && unit.statusHistory.find((s) => s.status === 'Llegada')
      return dt && dt.dateTime ? 'Llegada: ' + moment(dt.dateTime).format('HH:mm') : ''
    },
    getLiberado(unit) {
      var dt = unit.statusHistory && unit.statusHistory.find((s) => s.status === 'Libre')
      return dt && dt.dateTime ? 'Liberado: ' + moment(dt.dateTime).format('HH:mm') : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.incident-dispatch-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--kln-bg-surface);
  border-right: 1px solid var(--kln-border-dim);
}

.dispatch-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid var(--kln-border-dim);
  flex-shrink: 0;
}

.dispatch-header__titles {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.dispatch-header__title {
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text);
  letter-spacing: 0.02em;
}

.dispatch-header__sub {
  font-size: 10px;
  color: var(--kln-text-dim);
  font-weight: 500;
}

.dispatch-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dispatch-body::-webkit-scrollbar { width: 6px; }
.dispatch-body::-webkit-scrollbar-thumb {
  background: var(--kln-border-dim);
  border-radius: 3px;
}

.dispatch-empty {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--kln-text-secondary);
  padding: 12px;
}

.corp-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  background: var(--kln-bg-card);
  border: 1px solid var(--kln-border-dim);
  border-radius: 6px;
}

/* Cuando la corp está cerrada solo cambiamos el borde para señalizar el
   estado; el contenido sigue legible y operativo (asignar / cambiar
   estatus siguen disponibles). El badge "Cerrado" lleva el peso visual. */
.corp-card--closed {
  border-color: rgba(239, 83, 80, 0.3);
  background: rgba(239, 83, 80, 0.03);
}

/* Corp virtual PRUEBAS: borde dashed accent + tag DEV para que el
   operador no la confunda con una corp real del incidente. */
.corp-card--virtual {
  border-style: dashed;
  border-color: rgba(73, 196, 232, 0.4);
  background: rgba(73, 196, 232, 0.04);
}

.corp-card__dev-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 3px;
  background: var(--kln-accent);
  color: #0a0d11;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  vertical-align: middle;
}

/* Autocomplete buscador de devices en la corp virtual PRUEBAS */
.test-corp-search {
  margin-bottom: 6px;
}

.test-corp-search__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.test-corp-search__name {
  font-size: 12px;
  color: var(--kln-text);
  font-weight: 500;
}

.test-corp-search__status {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(158, 158, 158, 0.18);
  color: #9e9e9e;
}

.test-corp-search__status--online,
.test-corp-search__status--Online {
  background: rgba(76, 175, 80, 0.18);
  color: #4caf50;
}

.test-corp-search__status--offline,
.test-corp-search__status--Offline {
  background: rgba(158, 158, 158, 0.18);
  color: #9e9e9e;
}

.test-corp-search__status--alert,
.test-corp-search__status--Alert {
  background: rgba(239, 83, 80, 0.18);
  color: #ef5350;
}

.corp-card__head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.corp-card__titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.corp-card__name {
  font-size: 13px;
  font-weight: 700;
  color: var(--kln-text);
}

.corp-card__folio {
  font-size: 10px;
  color: var(--kln-text-muted);
  font-weight: 500;
}

.corp-card__status {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(76, 175, 80, 0.15);
  color: #4caf50;
  white-space: nowrap;
  flex-shrink: 0;
}

.corp-card__status--closed {
  background: rgba(239, 83, 80, 0.18);
  color: #ef5350;
}

.corp-card__kebab {
  flex-shrink: 0;
  margin-top: -4px;
}

.corp-card__assign-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background: transparent;
  color: var(--kln-accent);
  border: 1px dashed var(--kln-accent);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s;
}

.corp-card__assign-btn:hover {
  background: rgba(73, 196, 232, 0.08);
}

.corp-units {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.corp-units--empty {
  font-size: 11px;
  color: var(--kln-text-dim);
  font-style: italic;
  padding: 4px 0;
}

.unit-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  border-left: 2px solid var(--kln-border-normal);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.unit-row:hover {
  background: var(--kln-border-dim);
  border-left-color: var(--kln-accent);
}

.unit-row--active {
  background: rgba(73, 196, 232, 0.08);
  border-left-color: var(--kln-accent);
  border-left-width: 3px;
}

.unit-row__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.unit-row__name {
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text);
}

.unit-row__status {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 10px;
  border: 1px solid;
  background: transparent;
  cursor: default;
  font-family: inherit;
}

.unit-row__status--clickable {
  cursor: pointer;
  transition: filter 0.15s;
}

.unit-row__status--clickable:hover {
  filter: brightness(1.2);
}

.unit-row__times {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 10px;
}

.unit-time {
  font-family: 'Roboto Mono', monospace;
  color: var(--kln-text-muted);
}

.unit-time--despacho { color: #ef5350; }
.unit-time--llegada { color: #facc15; }
.unit-time--libre { color: #9e9e9e; }

/* Badge "12.3 km · 18 min · sin tráfico" debajo de la unit-row cuando
   esta tiene la ruta activa. Estética consistente con el accent del
   resto del panel (color azul/celeste, fondo soft). */
.route-badge {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  padding: 4px 8px;
  border-radius: 10px;
  background: rgba(73, 196, 232, 0.1);
  border: 1px solid rgba(73, 196, 232, 0.25);
  font-size: 11px;
  color: var(--kln-accent);
  letter-spacing: 0.02em;
}

.route-badge__distance,
.route-badge__duration {
  font-weight: 700;
  font-family: 'Roboto Mono', monospace;
}

.route-badge__sep {
  opacity: 0.5;
}

.route-badge__note {
  margin-left: 4px;
  color: var(--kln-text-dim);
  font-weight: 500;
  font-size: 10px;
}

/* Diálogos (assign / cancel / close) — comparten el mismo estilo base. */
.assign-dialog__title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid var(--kln-border-dim);
}

.assign-dialog__body {
  padding: 16px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.assign-dialog__readonly {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: var(--kln-text-muted);
  padding: 8px 10px;
  background: var(--kln-bg-card);
  border-radius: 4px;
}

.assign-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.assign-dialog__label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--kln-text-muted);
}

.assign-dialog__error {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #f44336;
  padding: 6px 8px;
  background: rgba(244, 67, 54, 0.08);
  border-left: 3px solid #f44336;
  border-radius: 4px;
}

.assign-dialog__footer {
  padding: 10px 16px !important;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid var(--kln-border-dim);
}
</style>
